import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import ImportModal from '../components/ImportModal';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [limit, setLimit] = useState(25);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newContact, setNewContact] = useState({ nombre: '', telefono: '', email: '' });
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editing, setEditing] = useState(null); // contact being edited

  // Debounce search - wait 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchContacts = useCallback(() => {
    api.get(`/contacts?page=${page}&limit=${limit}&search=${encodeURIComponent(debouncedSearch)}`)
      .then((res) => { setContacts(res.data.contacts); setTotal(res.data.total); setPages(res.data.pages); });
  }, [page, limit, debouncedSearch]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/contacts/sync-shopify');
      alert(`${data.synced} clientes sincronizados`);
      fetchContacts();
    } catch {
      alert('Error al sincronizar. Verifica la configuracion de Shopify.');
    }
    setSyncing(false);
  };

  const handleExportTopBuyers = async () => {
    const raw = window.prompt(
      'Rango a exportar (ej. "1-2000", "2001-4000", o solo "2000" para los primeros 2000)',
      '1-2000',
    );
    if (!raw) return;
    let from;
    let to;
    const rangeMatch = raw.trim().match(/^(\d+)\s*-\s*(\d+)$/);
    const singleMatch = raw.trim().match(/^(\d+)$/);
    if (rangeMatch) {
      from = parseInt(rangeMatch[1], 10);
      to = parseInt(rangeMatch[2], 10);
    } else if (singleMatch) {
      from = 1;
      to = parseInt(singleMatch[1], 10);
    } else {
      alert('Formato inválido. Usa "1-2000", "2001-4000" o "2000".');
      return;
    }
    if (!Number.isFinite(from) || !Number.isFinite(to) || from < 1 || to < from) {
      alert('Rango inválido: el primer número debe ser ≥ 1 y el segundo ≥ al primero.');
      return;
    }
    setExporting(true);
    try {
      const res = await api.get(`/contacts/export-top-buyers?from=${from}&to=${to}`, { responseType: 'blob', timeout: 600000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `top_compradores_${from}-${to}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Error al exportar';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.error || msg;
        } catch {}
      } else {
        msg = err.response?.data?.error || err.message || msg;
      }
      alert(msg);
    }
    setExporting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este contacto? Esta accion no se puede deshacer.')) return;
    try {
      await api.delete(`/contacts/${id}`);
      fetchContacts();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/contacts/${editing.id}`, {
        nombre: editing.nombre,
        telefono: editing.telefono,
        email: editing.email,
        blacklisted: editing.blacklisted,
        opt_in: editing.opt_in,
      });
      setEditing(null);
      fetchContacts();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar contacto');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1>Contactos <span style={{ fontSize: 14, color: '#888', fontWeight: 400 }}>({total})</span></h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSync} disabled={syncing} style={{
            padding: '8px 16px', border: '1px solid #3498db', color: '#3498db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 13,
          }}>{syncing ? 'Sincronizando...' : 'Sync Shopify'}</button>
          <button onClick={handleExportTopBuyers} disabled={exporting} title="Descarga un Excel del top compradores de Shopify, listo para importar" style={{
            padding: '8px 16px', border: '1px solid #f39c12', color: '#f39c12', borderRadius: 4, background: '#fff',
            cursor: exporting ? 'wait' : 'pointer', fontSize: 13,
          }}>{exporting ? 'Exportando…' : '⭐ Exportar top compradores'}</button>
          <button onClick={() => setShowAdd(!showAdd)} style={{
            padding: '8px 16px', border: '1px solid #4ecca3', color: '#4ecca3', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 13,
          }}>{showAdd ? 'Cancelar' : '+ Agregar'}</button>
          <button onClick={() => setShowImport(true)} style={{
            padding: '8px 16px', background: '#4ecca3', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13,
          }}>Importar</button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.post('/contacts', newContact);
            setNewContact({ nombre: '', telefono: '', email: '' });
            setShowAdd(false);
            fetchContacts();
          } catch (err) {
            alert(err.response?.data?.error || 'Error al crear contacto');
          }
        }} style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
          <input placeholder="Nombre" value={newContact.nombre} onChange={(e) => setNewContact({ ...newContact, nombre: e.target.value })} required
            style={{ flex: 1, padding: 10, border: '1px solid #ddd', borderRadius: 4 }} />
          <input placeholder="+5215512345678" value={newContact.telefono} onChange={(e) => setNewContact({ ...newContact, telefono: e.target.value })} required
            style={{ flex: 1, padding: 10, border: '1px solid #ddd', borderRadius: 4 }} />
          <input placeholder="Email (opcional)" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
            style={{ flex: 1, padding: 10, border: '1px solid #ddd', borderRadius: 4 }} />
          <button type="submit" style={{ padding: '10px 20px', background: '#4ecca3', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Guardar</button>
        </form>
      )}

      <input
        placeholder="Buscar por nombre o telefono..."
        value={search} onChange={(e) => setSearch(e.target.value)}
        style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, marginBottom: 16, boxSizing: 'border-box' }}
      />

      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
              <th style={{ padding: 12 }}>Nombre</th>
              <th>Telefono</th>
              <th>Email</th>
              <th>Origen</th>
              <th>WhatsApp</th>
              <th>Estado</th>
              <th style={{ width: 140 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const waBadge = {
                valid: { text: '✓ válido', bg: '#d4edda', color: '#155724' },
                no_whatsapp: { text: 'sin WA', bg: '#f8d7da', color: '#721c24' },
                filtered: { text: 'filtrado', bg: '#fff3cd', color: '#856404' },
              }[c.whatsapp_status];
              return (
              <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: 12 }}>{c.nombre}</td>
                <td>{c.telefono}</td>
                <td>{c.email || '-'}</td>
                <td><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: c.origen === 'shopify' ? '#d4edda' : '#e2e3e5' }}>{c.origen}</span></td>
                <td>
                  {waBadge ? (
                    <span title={c.whatsapp_checked_at ? `Detectado ${new Date(c.whatsapp_checked_at).toLocaleDateString()}` : ''}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: waBadge.bg, color: waBadge.color }}>
                      {waBadge.text}
                    </span>
                  ) : <span style={{ color: '#bbb', fontSize: 11 }}>—</span>}
                </td>
                <td>{c.blacklisted ? <span style={{ color: '#e74c3c' }}>Bloqueado</span> : <span style={{ color: '#2ecc71' }}>Activo</span>}</td>
                <td>
                  <button onClick={() => setEditing({ ...c })} style={{
                    padding: '4px 10px', background: '#3498db', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, marginRight: 6,
                  }}>Editar</button>
                  <button onClick={() => handleDelete(c.id)} style={{
                    padding: '4px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11,
                  }}>Eliminar</button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#888' }}>
          <span>Mostrar</span>
          <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer' }}>
            {[25, 50, 100, 250, 500, 1000].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span>por pagina</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ padding: '6px 16px', cursor: 'pointer' }}>Anterior</button>
          <span style={{ padding: '6px 0', color: '#888', fontSize: 13 }}>Pagina {page} de {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} style={{ padding: '6px 16px', cursor: 'pointer' }}>Siguiente</button>
        </div>
      </div>

      {showImport && <ImportModal onClose={() => setShowImport(false)} onSuccess={fetchContacts} />}

      {editing && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 30, maxWidth: 480, width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Editar contacto</h3>

            <label style={{ fontSize: 12, fontWeight: 600 }}>Nombre</label>
            <input
              value={editing.nombre || ''}
              onChange={(e) => setEditing({ ...editing, nombre: e.target.value })}
              style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, marginBottom: 12, boxSizing: 'border-box' }}
            />

            <label style={{ fontSize: 12, fontWeight: 600 }}>Telefono (formato E.164, ej: +525519879740)</label>
            <input
              value={editing.telefono || ''}
              onChange={(e) => setEditing({ ...editing, telefono: e.target.value })}
              style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, marginBottom: 12, boxSizing: 'border-box' }}
            />

            <label style={{ fontSize: 12, fontWeight: 600 }}>Email</label>
            <input
              value={editing.email || ''}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, marginBottom: 12, boxSizing: 'border-box' }}
            />

            <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!editing.opt_in}
                  onChange={(e) => setEditing({ ...editing, opt_in: e.target.checked })}
                />
                Opt-in (recibe campanas)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!editing.blacklisted}
                  onChange={(e) => setEditing({ ...editing, blacklisted: e.target.checked })}
                />
                Bloqueado (blacklist)
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)} style={{
                padding: '8px 20px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer',
              }}>Cancelar</button>
              <button onClick={handleUpdate} style={{
                padding: '8px 20px', border: 'none', borderRadius: 4, background: '#4ecca3', color: '#fff', cursor: 'pointer',
              }}>Guardar cambios</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
